import Inscription from "layouts/inscription";
import InscriptionAdmin from "layouts/inscription-admin";
import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { useAuth } from "context/authContext";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Pages
import Dashboard from "layouts/dashboard";
import Groupes from "layouts/groupes";
import Utilisateurs from "layouts/utilisateurs";
import Reservations from "layouts/reservations";
import MesReservations from "layouts/mes-reservations";
import Calendrier from "layouts/calendrier";
import Editions from "layouts/editions";
import OumrahSessions from "layouts/oumrah-sessions";
import OumrahInscriptions from "layouts/oumrah-inscriptions";
import OumrahDirecteur from "layouts/oumrah-directeur";
import OumrahSimulateur from "layouts/oumrah-simulateur";
import OumrahDashboard from "layouts/oumrah-dashboard";
import OumrahModeles from "layouts/oumrah-modeles";
import OumrahDocumentsTypes from "layouts/oumrah-documents-types";
import OumrahHebergement from "layouts/oumrah-hebergement";
import OumrahAccompagnateurs from "layouts/oumrah-accompagnateurs";
import OumrahBilan from "layouts/oumrah-bilan";
import OumrahRecap from "layouts/oumrah-recap";
import SignIn from "layouts/authentication/sign-in";
import Invitation from "layouts/invitation";
import MotDePasseOublie from "layouts/mot-de-passe-oublie";
import ReinitialiserMotDePasse from "layouts/reinitialiser-mot-de-passe";
import Profil from "layouts/profil";
import Invitations from "layouts/invitations";
import Feedbacks from "layouts/feedbacks";

const pageMap = {
  dashboard: Dashboard,
  groupes: Groupes,
  utilisateurs: Utilisateurs,
  profil: Profil,
  reservations: Reservations,
  "mes-reservations": MesReservations,
  "sign-in": SignIn,
  calendrier: Calendrier,
  editions: Editions,
  "oumrah-sessions": OumrahSessions,
  "oumrah-inscriptions": OumrahInscriptions,
  "oumrah-directeur": OumrahDirecteur,
  "oumrah-simulateur": OumrahSimulateur,
  "oumrah-dashboard": OumrahDashboard,
  "oumrah-modeles": OumrahModeles,
  "oumrah-documents-types": OumrahDocumentsTypes,
  "oumrah-hebergement": OumrahHebergement,
  "oumrah-accompagnateurs": OumrahAccompagnateurs,
  "oumrah-bilan": OumrahBilan,
  "oumrah-recap": OumrahRecap,
  "oumrah-recap-dir": OumrahRecap,
  invitations: Invitations,
  feedbacks: Feedbacks,
  "inscription-admin": InscriptionAdmin,
};

function ProtectedRoute({ component: Component, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/authentication/sign-in" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/authentication/sign-in" />;
  return <Component />;
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const filteredRoutes = routes.filter(
    (r) => !r.roles || (user && r.roles.includes(user.role))
  );

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.route) {
        const Component = pageMap[route.key];
        return (
          <Route
            exact
            path={route.route}
            element={
              route.roles ? (
                <ProtectedRoute component={Component} roles={route.roles} />
              ) : (
                <Component />
              )
            }
            key={route.key}
          />
        );
      }
      return null;
    });

  const defaultRoute =
    user?.role === "directeur" ? "/mes-reservations"
    : user?.role === "admin_oumrah" ? "/oumrah/sessions"
    : "/dashboard";

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brandName="Gestion SI APBIF"
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />

        </>
      )}
      <Routes>
        {getRoutes(filteredRoutes)}
        <Route path="/authentication/sign-in" element={<SignIn />} />
        <Route path="/invitation/:token" element={<Invitation />} />
        <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
        <Route path="/reinitialiser-mot-de-passe/:token" element={<ReinitialiserMotDePasse />} />
	<Route path="/inscription/:token" element={<Inscription />} />
        <Route
          path="*"
          element={<Navigate to={user ? defaultRoute : "/authentication/sign-in"} />}
        />
      </Routes>
    </ThemeProvider>
  );
}
